import React, { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import "../components/Items.css";
import "../components/Spinner.css";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function Zara() {
  const { search } = useLocation();
  const genderParam = new URLSearchParams(search).get("gender") || "Men";

  // 1) Memoize categories so they only change on genderParam
  const categories = useMemo(
    () => (genderParam === "Women" ? ["Clothing"] : ["Clothing"]),
    [genderParam]
  );

  const [cat, setCat] = useState("Clothing");
  const [deals, setDeals] = useState([]);
  const [error, setError] = useState(null);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setCat(categories[0]);
    setIndex(0);
  }, [genderParam, categories]);

  useEffect(() => {
    // whenever the category changes, reset carousel
    setIndex(0);

    async function fetchDeals() {
      try {
        // build your URL with query-params
        const url = new URL(
          "https://ra67ayqij6g7rtl6zimfcoeak40laacu.lambda-url.us-east-2.on.aws/"
        );
        url.search = new URLSearchParams({
          gender: genderParam,
          brand: "Zara",
          type: cat,
        });

        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const items = await res.json();

        // massage the data
        const mapped = items.map((item) => {
          let highResImg = item.image_url.replace(
            /w=\d+(?:&sh=\d+)?/g,
            "w=400&sh=800"
          );
          return {
            id: item.SK,
            link: item.link,
            imgUrl: highResImg,
            name: item.product_name,
            originalPrice: item.original_price,
            currentPrice: item.current_price,
          };
        });

        // pre-load images
        mapped.forEach((d) => {
          const img = new Image();
          img.src = d.imgUrl;
        });

        setDeals(mapped);
      } catch (e) {
        console.error(e);
        setError(e.toString());
      }
    }

    // call it once
    fetchDeals();
  }, [cat, genderParam]);

  const current = deals[index] || null;

  const prev = () => setIndex((i) => (i - 1 + deals.length) % deals.length);
  const next = () => setIndex((i) => (i + 1) % deals.length);

  return (
    <div className="brand-page">
      <nav className="categories">
        {categories.map((c) => (
          <button
            key={c}
            className={c === cat ? "active" : ""}
            onClick={() => {
              setCat(c);
              setIndex(0);
            }}
          >
            {c}
          </button>
        ))}
      </nav>

      {(!deals || deals.length === 0) && !error ? (
        <div className="trending_loading_container">
          <div className="spinner" />
          <p className="trending_loading_message">
            Retrieving sales data — please check back in a few minutes.
          </p>
        </div>
      ) : (
        <div className="carousel-container">
          <button className="arrow left" onClick={prev}>
            <ChevronLeft></ChevronLeft>
          </button>

          <AnimatePresence exitBeforeEnter>
            {current && (
              <motion.div
                key={current.id}
                className="zara"
                initial={{ opacity: 0, rotate: 5, scale: 0.8 }}
                animate={{ opacity: 1, rotate: 0, scale: 1 }}
                exit={{ opacity: 0, rotate: -5, scale: 0.8 }}
                transition={{ duration: 0.3 }}
              >
                <img src={current.imgUrl} alt={current.name} />

                <div className="info">
                  <h3>
                    <a
                      href={current.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="item-link"
                    >
                      {current.name}
                    </a>
                  </h3>
                  <p className="prices">
                    <span className="original">${current.originalPrice}</span>
                    <span className="current">${current.currentPrice}</span>
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <button className="arrow right" onClick={next}>
            <ChevronRight></ChevronRight>
          </button>
        </div>
      )}
    </div>
  );
}
